import debounce from 'debounce';
import { useCallback, useEffect, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import { create } from 'zustand';
import { SOCKET_OPTIONS, WsNamespace, WsStreamType } from '../constants/ws';
import { apiWsUrl, wsInvalidationDelayMs } from '../lib/config';
import { keys } from '../lib/objectHelpers';
import {
  routeBookDepthMessage,
  routeMarketPriceMessage,
  routeOrderFillMessage,
  routeOrderUpdateMessage,
  routeSubaccountLiquidationMessage,
  routeTokenTransferMessage,
  subscribeToStream,
} from '../lib/wsHelpers';
import { OrderFills } from '../types/fills';
import { SubaccountLiquidation } from '../types/liquidation';
import { BookDepth } from '../types/orderbook';
import { WsOrder } from '../types/orders';
import { MarketPrice } from '../types/prices';
import { WsTokenTransfers } from '../types/tokenTransfer';

const LOGGING = false;

export const debugLog = (stream: StreamType | '', ...args: Parameters<typeof console.log>) => {
  if (LOGGING) {
    console.log(`[useWebsocketStore ${stream}]`, ...args);
  }
};

// Stream data types
export type StreamDataMap = {
  [WsStreamType.BOOK_DEPTH]: BookDepth | null;
  [WsStreamType.ORDER_FILL]: OrderFills;
  [WsStreamType.MARKET_PRICE]: MarketPrice;
  [WsStreamType.ORDER_UPDATE]: WsOrder;
  [WsStreamType.SUBACCOUNT_LIQUIDATION]: SubaccountLiquidation;
  [WsStreamType.TOKEN_TRANSFER]: WsTokenTransfers;
};

export type StreamType = keyof StreamDataMap;

// Parameters required for each stream type
export type SubscribeParams = {
  [WsStreamType.BOOK_DEPTH]: {
    type: WsStreamType.BOOK_DEPTH;
    productId: string;
  };
  [WsStreamType.ORDER_FILL]: {
    type: WsStreamType.ORDER_FILL;
    subaccountId: string;
  };
  [WsStreamType.MARKET_PRICE]: {
    type: WsStreamType.MARKET_PRICE;
    productId: string;
  };
  [WsStreamType.ORDER_UPDATE]: {
    type: WsStreamType.ORDER_UPDATE;
    subaccountId: string;
  };
  [WsStreamType.SUBACCOUNT_LIQUIDATION]: {
    type: WsStreamType.SUBACCOUNT_LIQUIDATION;
    subaccountId: string;
  };
  [WsStreamType.TOKEN_TRANSFER]: {
    type: WsStreamType.TOKEN_TRANSFER;
    subaccountId: string;
  };
};

// Handler function type for each stream
export type StreamHandler<T extends StreamType> = (data: StreamDataMap[T]) => void;

export interface Subscription<T extends StreamType> {
  params: SubscribeParams[T];
  handlers: StreamHandler<T>[];
}

// Websocket store interface
interface WebsocketStore {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  subscriptions: {
    [K in WsStreamType]: Map<string, Subscription<K>>;
  };
  bookDepthLastProcessedTime: Record<string, number>;

  reconnect: () => void;
  connect: () => void;
  refreshBookDepth: (productId: string) => void;
  subscribe: <T extends StreamType>(
    streamType: T,
    params: SubscribeParams[T],
    handler: StreamHandler<T>
  ) => () => void;
}

const initialState = {
  socket: null,
  isConnected: false,
  isConnecting: false,
  subscriptions: {
    [WsStreamType.BOOK_DEPTH]: new Map(),
    [WsStreamType.ORDER_FILL]: new Map(),
    [WsStreamType.MARKET_PRICE]: new Map(),
    [WsStreamType.ORDER_UPDATE]: new Map(),
    [WsStreamType.SUBACCOUNT_LIQUIDATION]: new Map(),
    [WsStreamType.TOKEN_TRANSFER]: new Map(),
  },
  bookDepthLastProcessedTime: {},
};

export const useWebsocketStore = create<WebsocketStore>((set, get) => ({
  ...initialState,

  connect: () => {
    const { socket, isConnecting } = get();
    debugLog('', 'Attempting to connect to WebSocket');

    // Don't create multiple connections
    if (socket?.connected || isConnecting) return;
    if (socket) {
      socket.removeAllListeners();
      set({ socket: null });
    }

    set({ isConnecting: true });

    const wsUrl = new URL(WsNamespace.STREAM, apiWsUrl);

    const newSocket = io(wsUrl.toString(), SOCKET_OPTIONS);

    newSocket.on('connect', () => {
      debugLog('', 'Socket connected');

      set({ isConnected: true, isConnecting: false });

      // Connection established, subscribe to all streams
      const { subscriptions } = get();
      keys(subscriptions).forEach((key) => {
        debugLog(key, `Subscribing to ${key}`);
        subscriptions[key].forEach((sub) => {
          debugLog(key, 'Subscribing to', sub);
          newSocket.emit('subscribe', sub.params);
        });
      });
    });

    newSocket.on('disconnect', (reason) => {
      debugLog('', 'Socket disconnected:', reason);
      set({ isConnected: false });
    });

    newSocket.on('error', (err) => {
      console.error('Socket error:', err);
      set({ isConnected: false, isConnecting: false });
    });

    setupStreamHandlers(newSocket);
    newSocket.connect();
    set({ socket: newSocket });
  },

  reconnect: () => {
    const { socket, connect, subscriptions } = get();

    // Clear stateful stream data before reconnecting
    subscriptions[WsStreamType.BOOK_DEPTH].forEach((subscription) => {
      subscription.handlers.forEach((handler) => {
        handler(null);
      });
    });

    socket?.removeAllListeners();
    socket?.disconnect();

    // Don't reset the entire state - just update connection status
    // This preserves our subscription Maps with all the handlers
    set({
      socket: null,
      isConnected: false,
      isConnecting: false,
      bookDepthLastProcessedTime: {},
    });

    connect();
  },

  refreshBookDepth: (productId: string) => {
    const { socket, subscriptions } = get();

    subscriptions[WsStreamType.BOOK_DEPTH].forEach((subscription) => {
      if (subscription.params.productId === productId) {
        subscription.handlers.forEach((handler) => {
          handler(null);
        });

        if (socket?.connected) {
          socket.emit('unsubscribe', subscription.params);
          socket.emit('subscribe', subscription.params);
        }
      }
    });
  },

  subscribe: <T extends StreamType>(
    streamType: T,
    params: SubscribeParams[T],
    handler: StreamHandler<T>
  ) => {
    const state = get();

    // Initialize connection if not already connected
    if (!state.socket) {
      state.connect();
    }
    return subscribeToStream(streamType, params, handler, () => {
      if (streamType === WsStreamType.BOOK_DEPTH && 'productId' in params) {
        (handler as StreamHandler<WsStreamType.BOOK_DEPTH>)(null);
      }
    });
  },
}));

// Set up handlers for each stream type
function setupStreamHandlers(socket: Socket) {
  return [
    { streamType: WsStreamType.BOOK_DEPTH, routeHandler: routeBookDepthMessage },
    { streamType: WsStreamType.ORDER_FILL, routeHandler: routeOrderFillMessage },
    { streamType: WsStreamType.MARKET_PRICE, routeHandler: routeMarketPriceMessage },
    { streamType: WsStreamType.ORDER_UPDATE, routeHandler: routeOrderUpdateMessage },
    {
      streamType: WsStreamType.SUBACCOUNT_LIQUIDATION,
      routeHandler: routeSubaccountLiquidationMessage,
    },
    { streamType: WsStreamType.TOKEN_TRANSFER, routeHandler: routeTokenTransferMessage },
  ].forEach(({ streamType, routeHandler }) => {
    socket.on(streamType, (data: unknown) => {
      debugLog(streamType, `Received message`);
      try {
        routeHandler(data);
      } catch (error) {
        console.error(`Error parsing ${streamType} data:`, data);
        console.error(error);
      }
    });
  });
}

const useGenericSubscribe = <T extends StreamType>(
  streamType: T,
  params: SubscribeParams[T] | undefined,
  handler: StreamHandler<T>
) => {
  const subscribe = useWebsocketStore((state) => state.subscribe);
  useEffect(() => {
    if (!params) return () => {};
    return subscribe(streamType, params, handler);
  }, [subscribe, params, streamType, handler]);
};

// Convenience hooks for each stream type
export function useBookDepth(
  productId: string | undefined,
  handler: StreamHandler<typeof WsStreamType.BOOK_DEPTH>
) {
  const memoizedHandler = useCallback(handler, [handler]);
  const params = useMemo(
    () => (productId ? ({ type: WsStreamType.BOOK_DEPTH, productId } as const) : undefined),
    [productId]
  );
  useGenericSubscribe(WsStreamType.BOOK_DEPTH, params, memoizedHandler);
}

export function useOrderFill(
  subaccountId: string | undefined,
  handler: StreamHandler<typeof WsStreamType.ORDER_FILL>
) {
  const debouncedHandler = useMemo(() => debounce(handler, wsInvalidationDelayMs), [handler]);
  const params = useMemo(
    () => (subaccountId ? ({ type: WsStreamType.ORDER_FILL, subaccountId } as const) : undefined),
    [subaccountId]
  );
  useGenericSubscribe(WsStreamType.ORDER_FILL, params, debouncedHandler);
}

export function useOrderUpdate(
  subaccountId: string | undefined,
  handler: StreamHandler<typeof WsStreamType.ORDER_UPDATE>
) {
  const debouncedHandler = useMemo(() => debounce(handler, wsInvalidationDelayMs), [handler]);
  const params = useMemo(
    () => (subaccountId ? ({ type: WsStreamType.ORDER_UPDATE, subaccountId } as const) : undefined),
    [subaccountId]
  );
  useGenericSubscribe(WsStreamType.ORDER_UPDATE, params, debouncedHandler);
}

export function useMarketPrice(
  productId: string | undefined,
  handler: StreamHandler<typeof WsStreamType.MARKET_PRICE>
) {
  const memoizedHandler = useCallback(handler, [handler]);
  const params = useMemo(
    () => (productId ? ({ type: WsStreamType.MARKET_PRICE, productId } as const) : undefined),
    [productId]
  );
  useGenericSubscribe(WsStreamType.MARKET_PRICE, params, memoizedHandler);
}

export function useSubaccountLiquidation(
  subaccountId: string | undefined,
  handler: StreamHandler<typeof WsStreamType.SUBACCOUNT_LIQUIDATION>
) {
  const debouncedHandler = useMemo(() => debounce(handler, wsInvalidationDelayMs), [handler]);
  const params = useMemo(
    () =>
      subaccountId
        ? ({ type: WsStreamType.SUBACCOUNT_LIQUIDATION, subaccountId } as const)
        : undefined,
    [subaccountId]
  );
  useGenericSubscribe(WsStreamType.SUBACCOUNT_LIQUIDATION, params, debouncedHandler);
}

export function useTokenTransfer(
  subaccountId: string | undefined,
  handler: StreamHandler<typeof WsStreamType.TOKEN_TRANSFER>
) {
  const debouncedHandler = useMemo(() => debounce(handler, wsInvalidationDelayMs), [handler]);
  const params = useMemo(
    () =>
      subaccountId ? ({ type: WsStreamType.TOKEN_TRANSFER, subaccountId } as const) : undefined,
    [subaccountId]
  );
  useGenericSubscribe(WsStreamType.TOKEN_TRANSFER, params, debouncedHandler);
}
